"use client";

import Link from "next/link";
import { type FormEventHandler, useEffect, useState } from "react";
import { GoogleIcon } from "@/components/Icons";
import { Button } from "@/components/ui/design-system/button";
import { Input } from "@/components/ui/design-system/input";
import { AuthenticationStep } from "@/lib/analytics/segmentEvents";
import { trackAuthFlow } from "@/lib/analytics/trackEvents";
import { formatOAuthDiscoveryStartURL } from "@/lib/StytchLib";
import { toast } from "./ui/use-toast";

const STATUS = {
  INIT: 0,
  SENT: 1,
  ERROR: 2,
};

const RESEND_COOLDOWN = 30;

const isValidEmail = (emailValue: string) => {
  const regex = /\S+@\S+\.\S+/;
  return regex.test(emailValue);
};

type EmailLoginProps = React.PropsWithChildren<{
  onSubmit: (email: string) => Promise<{ status_code: number }>;
}>;
export const EmailLoginForm = ({ onSubmit }: EmailLoginProps) => {
  const [emlSent, setEMLSent] = useState(STATUS.INIT);
  const [email, setEmail] = useState("");
  const [isDisabled, setIsDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [cooldownTime, setCooldownTime] = useState(0);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (cooldownTime > 0) {
      timer = setInterval(() => {
        setCooldownTime((time) => Math.max(0, time - 1));
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [cooldownTime]);

  useEffect(() => {
    setIsDisabled(!isValidEmail(email));
  }, [email]);

  const onSubmitFormHandler: FormEventHandler = async (e) => {
    e.preventDefault();
    if (isDisabled) {
      return;
    }
    setIsDisabled(true);
    setIsLoading(true);

    if (isValidEmail(email)) {
      try {
        trackAuthFlow({
          step: AuthenticationStep.DISCOVERY_PAGE,
          additionalParams: {
            event: "login attempt",
            type: "email_link",
            status: "submitted",
            email: email,
            emailDomain: email.split("@")[1],
          },
        });

        const resp = await onSubmit(email);
        if (resp.status_code === 200) {
          setEMLSent(STATUS.SENT);
          setCooldownTime(RESEND_COOLDOWN);

          trackAuthFlow({
            step: AuthenticationStep.DISCOVERY_PAGE,
            additionalParams: {
              event: "login attempt",
              type: "email_link",
              status: "email sent",
              email: email,
              emailDomain: email.split("@")[1],
            },
          });

          toast({
            title: "Email sent",
            description: "Please check your inbox for the login link.",
          });
        } else {
          setEMLSent(STATUS.ERROR);

          trackAuthFlow({
            step: AuthenticationStep.DISCOVERY_PAGE,
            additionalParams: {
              event: "login attempt",
              type: "email_link",
              status: "failed",
              email: email,
              emailDomain: email.split("@")[1],
              errorCode: resp.status_code,
            },
          });

          toast({
            title: "Failed to send email",
            description: "Please try again.",
            variant: "destructive",
          });
        }
      } catch (error) {
        setEMLSent(STATUS.ERROR);

        trackAuthFlow({
          step: AuthenticationStep.DISCOVERY_PAGE,
          additionalParams: {
            event: "login attempt",
            type: "email_link",
            status: "error",
            email: email,
            emailDomain: email.split("@")[1],
            error: error instanceof Error ? error.message : "Unknown error",
          },
        });

        toast({
          title: "Something went wrong",
          description: "Please try again later.",
          variant: "destructive",
        });
      }
    } else {
      toast({
        title: "Please enter a valid email address",
        variant: "destructive",
      });
    }
    setIsLoading(false);
  };

  const handleTryAgain: FormEventHandler = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      trackAuthFlow({
        step: AuthenticationStep.DISCOVERY_PAGE,
        additionalParams: {
          event: "login attempt",
          type: "email_link",
          status: "resend submitted",
          email: email,
          emailDomain: email.split("@")[1],
        },
      });

      const resp = await onSubmit(email);
      if (resp.status_code === 200) {
        setCooldownTime(RESEND_COOLDOWN);

        trackAuthFlow({
          step: AuthenticationStep.DISCOVERY_PAGE,
          additionalParams: {
            event: "login attempt",
            type: "email_link",
            status: "resend success",
            email: email,
            emailDomain: email.split("@")[1],
          },
        });

        toast({
          title: "Email sent again",
          description: "Please check your inbox for the login link.",
        });
      } else {
        setEMLSent(STATUS.ERROR);

        trackAuthFlow({
          step: AuthenticationStep.DISCOVERY_PAGE,
          additionalParams: {
            event: "login attempt",
            type: "email_link",
            status: "resend failed",
            email: email,
            emailDomain: email.split("@")[1],
            errorCode: resp.status_code,
          },
        });

        toast({
          title: "Failed to send email",
          description: "Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      setEMLSent(STATUS.ERROR);

      trackAuthFlow({
        step: AuthenticationStep.DISCOVERY_PAGE,
        additionalParams: {
          event: "login attempt",
          type: "email_link",
          status: "resend error",
          email: email,
          emailDomain: email.split("@")[1],
          error: error instanceof Error ? error.message : "Unknown error",
        },
      });

      toast({
        title: "Something went wrong",
        description: "Please try again later.",
        variant: "destructive",
      });
    }

    setIsLoading(false);
  };

  const handleUseDifferentEmail = () => {
    setEMLSent(STATUS.INIT);
    setEmail("");
    setCooldownTime(0);
  };

  const handleGoogleLogin = () => {
    trackAuthFlow({
      step: AuthenticationStep.DISCOVERY_PAGE,
      additionalParams: {
        event: "login attempt",
        type: "google_oauth",
        status: "initiated",
      },
    });
  };

  const hostDomain =
    typeof window !== "undefined" ? window.location.origin : "";
  const googleAuthUrl = formatOAuthDiscoveryStartURL(hostDomain, "google");

  return (
    <div className="flex flex-col w-full gap-nf-ds-6">
      {emlSent === STATUS.INIT && (
        <>
          <Button variant="pill-outline" asChild className="w-full gap-nf-ds-3">
            <Link href={googleAuthUrl} onClick={handleGoogleLogin}>
              <GoogleIcon className="w-[18px] h-[18px] shrink-0" />
              <span>Continue with Google</span>
            </Link>
          </Button>

          <div className="relative flex items-center justify-center">
            <div className="grow h-px bg-nf-ds-gray-300/60" />
            <span className="px-nf-ds-4 font-nf-ds-mono text-[10.5px] uppercase tracking-[0.12em] text-nf-ds-gray-500">
              or
            </span>
            <div className="grow h-px bg-nf-ds-gray-300/60" />
          </div>
        </>
      )}

      {emlSent !== STATUS.INIT && (
        <div className="flex flex-col gap-nf-ds-2">
          <h2 className="font-nf-ds-serif text-[28px] leading-[1.1] tracking-[-0.01em] text-nf-ds-black">
            {emlSent === STATUS.SENT && "Check your email"}
            {emlSent === STATUS.ERROR && "Something went wrong"}
          </h2>
          {emlSent === STATUS.SENT && (
            <p className="font-nf-ds-sans text-[14px] text-nf-ds-gray-600">
              We've sent a login link to {email}.
            </p>
          )}
        </div>
      )}

      <form
        onSubmit={
          emlSent === STATUS.INIT ? onSubmitFormHandler : handleTryAgain
        }
        className="flex flex-col gap-nf-ds-5"
      >
        <div className="flex flex-col gap-nf-ds-2">
          <label
            htmlFor="email"
            className="font-nf-ds-mono text-[10.5px] uppercase tracking-[0.12em] text-nf-ds-gray-600"
          >
            Email
          </label>
          <Input
            id="email"
            name="email"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            disabled={emlSent !== STATUS.INIT}
            className="h-12 rounded-nf-ds-2xl bg-nf-ds-white border-nf-ds-gray-200 px-nf-ds-5 shadow-nf-ds-xs"
          />
        </div>

        <Button
          type="submit"
          variant="pill-primary"
          disabled={
            (emlSent === STATUS.INIT && isDisabled) ||
            (emlSent === STATUS.SENT && cooldownTime > 0) ||
            isLoading
          }
          className="w-full mt-nf-ds-2"
        >
          {isLoading ? (
            "Sending…"
          ) : (
            <>
              {emlSent === STATUS.INIT && (
                <span className="inline-flex items-center gap-nf-ds-2">
                  Continue
                  <span aria-hidden>→</span>
                </span>
              )}
              {emlSent === STATUS.SENT &&
                (cooldownTime > 0
                  ? `Resend in ${cooldownTime}s`
                  : "Resend email")}
              {emlSent === STATUS.ERROR && "Try again"}
            </>
          )}
        </Button>
      </form>

      {emlSent === STATUS.SENT && (
        <button
          type="button"
          onClick={handleUseDifferentEmail}
          className="self-center font-nf-ds-sans text-[13px] font-[500] text-nf-ds-purple-product hover:text-nf-ds-purple-700"
        >
          Use a different email
        </button>
      )}
    </div>
  );
};
